import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { AuthLayout } from "@/components/auth/auth-layout";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { formatAuthError } from "@/lib/auth-errors";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

type ProviderId = "google" | "apple" | "azure";

type ProviderConfig = {
  id: ProviderId;
  label: string;
  icon: ReactNode;
  className: string;
};

const PROVIDERS: ProviderConfig[] = [
  {
    id: "google",
    label: "Continue with Google",
    className:
      "bg-white text-slate-900 hover:bg-slate-100 active:scale-[0.99] font-semibold shadow-lg shadow-black/30",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
      </svg>
    ),
  },
  {
    id: "apple",
    label: "Continue with Apple",
    className:
      "bg-white/[0.04] text-white border border-white/10 hover:bg-white/[0.08] hover:border-white/20 active:scale-[0.99] font-medium",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true" fill="currentColor">
        <path d="M17.05 20.28c-.98.95-2.05 1.72-3.21 1.72-1.14 0-1.52-.71-2.9-.71s-1.81.69-2.9.71c-1.12.02-2.11-.69-3.17-1.72C3.3 18.73 2 15.68 2 12.51c0-4.1 2.65-6.26 5.17-6.26 1.34 0 2.44.82 3.19.82.72 0 1.96-.86 3.42-.86 1.8 0 3.32.94 4.14 2.21-3.52 1.54-2.93 6.44.53 7.86zM13.67 4.54c0-2.31 1.92-4.19 4.28-4.21.05 2.24-1.83 4.41-4.28 4.21z" />
      </svg>
    ),
  },
  {
    id: "azure",
    label: "Continue with Microsoft",
    className:
      "bg-white/[0.04] text-white border border-white/10 hover:bg-white/[0.08] hover:border-white/20 active:scale-[0.99] font-medium",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
        <path fill="#F25022" d="M3 3h8v8H3z" />
        <path fill="#7FBA00" d="M13 3h8v8h-8z" />
        <path fill="#00A4EF" d="M3 13h8v8H3z" />
        <path fill="#FFB900" d="M13 13h8v8h-8z" />
      </svg>
    ),
  },
];

function LoginPage() {
  const { session, loading } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState<ProviderId | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && session) navigate({ to: "/dashboard" });
  }, [loading, session, navigate]);

  async function signIn(provider: ProviderId) {
    setError(null);
    setPending(provider);
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/dashboard` },
    });
    if (err) {
      setPending(null);
      setError(formatAuthError(err.message));
    }
  }

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to continue to ClipsFlow"
      footer={
        <span>
          By continuing you agree to our{" "}
          <a href="/terms" className="text-slate-300 underline-offset-4 hover:underline">Terms</a>
          {" "}and{" "}
          <a href="/privacy" className="text-slate-300 underline-offset-4 hover:underline">Privacy Policy</a>.
        </span>
      }
    >
      <div className="grid gap-2.5">
        {PROVIDERS.map((p) => {
          const isPending = pending === p.id;
          const isDisabled = pending !== null;
          return (
            <button
              key={p.id}
              type="button"
              disabled={isDisabled}
              onClick={() => signIn(p.id)}
              aria-busy={isPending}
              className={`group relative flex min-h-11 w-full items-center justify-center gap-3 rounded-xl px-4 py-3 text-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 disabled:cursor-not-allowed ${
                isDisabled && !isPending ? "opacity-50" : ""
              } ${p.className}`}
            >
              <span className="flex h-5 w-5 items-center justify-center">{p.icon}</span>
              <span>{p.label}</span>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin opacity-80" /> : null}
            </button>
          );
        })}

        {error ? (
          <div role="alert" className="mt-1 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">
            <span aria-hidden="true" className="mt-1.5 inline-block h-1.5 w-1.5 flex-none rounded-full bg-red-400" />
            <span>{error}</span>
          </div>
        ) : null}

        <div className="relative my-2 flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-slate-500">
          <span className="h-px flex-1 bg-white/10" />
          <span>Encrypted sign-in</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <p className="text-center text-xs text-slate-400">
          New here? Your account is created automatically on first sign-in.
        </p>
      </div>
    </AuthLayout>
  );
}
